import { SBaseDisplay } from '@summit/core';

import { SDate } from '@summit/core';
import { EPosition, MoneyFormatter, SummitFormattedText } from '@summit/models';
import { EboObject } from '../../base-classes/ebo-object';
import { City } from '../city';
import { ELocation } from '../location/e-location';
import { LocationSource } from '../location/location-source.enum';
import { LocationType } from '../location/location-type.enum';
import { StatePayrollRate } from './state-payroll-rate';
import { StateWage } from './state-wage';

export class State extends EboObject implements SBaseDisplay {
  static border_dict: { [key: string]: string[] } = {
    AL: ['FL', 'GA', 'MS', 'TN'],
    AK: [],
    AZ: ['CA', 'NV', 'NM', 'UT'],
    AR: ['LA', 'MS', 'MO', 'OK', 'TN', 'TX'],
    CA: ['AZ', 'NV', 'OR'],
    CO: ['KS', 'NE', 'NM', 'OK', 'UT', 'WY'],
    CT: ['MA', 'NY', 'RI'],
    DC: ['VA', 'MD'],
    DE: ['MD', 'NJ', 'PA'],
    FL: ['AL', 'GA'],
    GA: ['AL', 'FL', 'NC', 'SC', 'TN'],
    HI: [],
    ID: ['MT', 'NV', 'OR', 'UT', 'WA', 'WY'],
    IL: ['IN', 'IA', 'MI', 'KY', 'MO', 'WI'],
    IN: ['IL', 'KY', 'MI', 'OH'],
    IA: ['IL', 'MN', 'MO', 'NE', 'SD', 'WI'],
    KS: ['CO', 'MO', 'NE', 'OK'],
    KY: ['IL', 'IN', 'MO', 'OH', 'TN', 'VA', 'WV'],
    LA: ['AR', 'MS', 'TX'],
    ME: ['NH'],
    MD: ['DC', 'DE', 'PA', 'VA', 'WV'],
    MA: ['CT', 'NH', 'NY', 'RI', 'VT'],
    MI: ['IN', 'OH', 'WI'],
    MN: ['IA', 'MI', 'ND', 'SD', 'WI'],
    MS: ['AL', 'AR', 'LA', 'TN'],
    MO: ['AR', 'IL', 'IA', 'KS', 'KY', 'NE', 'OK', 'TN'],
    MT: ['ID', 'ND', 'SD', 'WY'],
    NE: ['CO', 'IA', 'KS', 'MO', 'SD', 'WY'],
    NV: ['AZ', 'CA', 'ID', 'OR', 'UT'],
    NH: ['ME', 'MA', 'VT'],
    NJ: ['DE', 'NY', 'PA'],
    NM: ['AZ', 'CO', 'OK', 'TX'],
    NY: ['CT', 'MA', 'NJ', 'PA', 'VT'],
    NC: ['GA', 'SC', 'TN', 'VA'],
    ND: ['MN', 'MT', 'SD'],
    OH: ['IN', 'KY', 'MI', 'PA', 'WV'],
    OK: ['AR', 'CO', 'KS', 'MO', 'NM', 'TX'],
    OR: ['CA', 'ID', 'NV', 'WA'],
    PA: ['DE', 'MD', 'NJ', 'NY', 'OH', 'WV'],
    RI: ['CT', 'MA'],
    SC: ['GA', 'NC'],
    SD: ['IA', 'MN', 'MT', 'NE', 'ND', 'WY'],
    TN: ['AL', 'AR', 'GA', 'KY', 'MS', 'MO', 'NC', 'VA'],
    TX: ['AR', 'LA', 'NM', 'OK'],
    UT: ['AZ', 'CO', 'ID', 'NV', 'WY'],
    VT: ['MA', 'NH', 'NY'],
    VA: ['DA', 'KY', 'MD', 'NC', 'TN', 'WV', 'DC'],
    WA: ['ID', 'OR'],
    WV: ['KY', 'MD', 'OH', 'PA', 'VA'],
    WI: ['IL', 'IA', 'MI', 'MN'],
    WY: ['CO', 'ID', 'MT', 'NE', 'SD', 'UT']
  };

  name: string;
  abbreviation: string;
  wages: StateWage[];
  payrollRates: StatePayrollRate[];
  cities: City[];
  locations: ELocation[];
  //temp
  boundaryString: string;

  //hack move from schema
  get minWage(): string {
    return '';
  }

  constructor(jsonData?: Partial<State>) {
    super(jsonData);
    if (jsonData.wages) {
      this.wages = jsonData.wages
        .map((data) => new StateWage(data))
        .sort((e1, e2) => -1 * e1.effectiveDate.diff(e2.effectiveDate));
    }
    if (jsonData.payrollRates) this.payrollRates = jsonData.payrollRates.map((data) => new StatePayrollRate(data));
    if (jsonData.cities) this.cities = jsonData.cities.map((data) => new City(data));
    if (jsonData.locations) this.locations = jsonData.locations.map((data) => new ELocation(data));
    this.wages ??= [];
    this.payrollRates ??= [];
    this.cities ??= [];
    this.locations ??= [];
  }

  toShortString(): string {
    return `${this.abbreviation} - ${this.name}`;
  }

  get minWageFormatted(): SummitFormattedText {
    const response = new SummitFormattedText();
    for (const entry of this.wages) {
      if (entry.effectiveDate) {
        response.AddLine(`${entry.effectiveDate.format('M/DD/YYYY')} - ${MoneyFormatter(entry.minimumWage)}\n`);
        if (entry.effectiveDate.year() < SDate.getNormalized().year()) {
          break;
        }
      }
    }
    return response;
  }

  get locationCount(): number {
    return this.locations.length;
  }

  addPolygon(rawBoundaries: number[][][]) {
    rawBoundaries
      .sort((a, b) => a.length - b.length) //putting most complex first
      .forEach((boundary) => {
        //console.log('Adding location based on:', boundary);
        const dataArray = boundary.length < 3 ? boundary[0] : boundary;
        const newLocation = new ELocation({
          //stateId: this.id,
          name: this.name,
          source: LocationSource.USCensus,
          type: LocationType.State,
          geometry: dataArray.map((data) => new EPosition({ longitude: data[0], latitude: data[1] }))
        });
        //Not a polygon if less than 3 points
        if (newLocation.geometry.length >= 3) this.locations.push(newLocation);
      });
  }

  is_drivable(toState: string) {
    return State.border_dict[this.abbreviation]?.includes(toState) ?? false;
  }
}
